import { useMemo, useState } from "react";
import type { LatLngBounds } from "leaflet";
import { useMap, useMapEvents } from "react-leaflet";
import { pickupLocationPolygons, polygonsBounds, polygonsCenter } from "./geofence-polygons";

export { pickupLocationPolygons, polygonsCenter };
import { METERS_PER_DEGREE_LAT } from "./geo-distance";

export { distanceMeters } from "./geo-distance";

/** Geofences appear here and not before; at z17 the median 14m yard is 16px.
 *  Sizing the swap per shape instead surfaced the частный сектор road strips
 *  first and the real yards last. */
export const GEOFENCE_MIN_ZOOM = 17;

/** Unclustering materialises every pin at once, so it happens where the
 *  viewport is small enough to hold few — the same zoom shapes begin at. */
export const CLUSTER_MAX_ZOOM = GEOFENCE_MIN_ZOOM;

/** Leaflet focuses a clicked path, and a focus ring on SVG is drawn as its
 *  bounding box. Kept for keyboard focus, dropped for mouse. */
export const GEOFENCE_CLASS = "[&:focus:not(:focus-visible)]:outline-none";

export interface GeofenceItem {
  id: number | string | bigint;
  lat: number;
  lon: number;
  name?: string;
  geometry?: unknown;
  /** Draws its geofence at geofence zoom and nothing otherwise; others carry the pins. */
  shapeOnly?: boolean;
}

interface Bounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

export interface GeofenceShape<T extends GeofenceItem> {
  item: T;
  /** One entry per polygon; within it, ring 0 is the outline and the rest holes. */
  polygons: [number, number][][][];
  bounds: Bounds | null;
}

export interface Viewport {
  zoom: number;
  bounds: LatLngBounds;
}

/** Tracks pan as well as zoom, so off-screen geofences can be skipped. */
export const useMapViewport = (): Viewport => {
  const map = useMap();
  const read = () => ({ zoom: map.getZoom(), bounds: map.getBounds() });
  const [viewport, setViewport] = useState<Viewport>(read);
  useMapEvents({ zoomend: () => setViewport(read()), moveend: () => setViewport(read()) });
  return viewport;
};

/** Zoom alone, for callers that must not re-render on pan. */
export const useZoomLevel = (): number => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  return zoom;
};

// Ray cast over one ring of [lat, lon].
const ringContains = (ring: [number, number][], lat: number, lon: number) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [latI, lonI] = ring[i];
    const [latJ, lonJ] = ring[j];
    if (latI > lat !== latJ > lat && lon < ((lonJ - lonI) * (lat - latI)) / (latJ - latI) + lonI) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Whether a point lies inside the polygons, or within `toleranceM` of their
 * outline. Distances are planar: fine at yard scale, not across a city.
 */
export const isNearPolygons = (
  lat: number,
  lon: number,
  polygons: [number, number][][][],
  toleranceM: number,
): boolean => {
  const metersPerDegreeLon = METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);
  // Point → segment distance, in metres, with the point as the origin.
  const toSegment = (a: [number, number], b: [number, number]) => {
    const ax = (a[1] - lon) * metersPerDegreeLon;
    const ay = (a[0] - lat) * METERS_PER_DEGREE_LAT;
    const bx = (b[1] - lon) * metersPerDegreeLon;
    const by = (b[0] - lat) * METERS_PER_DEGREE_LAT;
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSq));
    return Math.hypot(ax + t * dx, ay + t * dy);
  };

  return polygons.some(([outline, ...holes]) => {
    if (!outline) return false;
    if (ringContains(outline, lat, lon) && !holes.some((hole) => ringContains(hole, lat, lon))) {
      return true;
    }
    return [outline, ...holes].some((ring) =>
      ring.some((point, i) => toSegment(point, ring[(i + 1) % ring.length]) <= toleranceM),
    );
  });
};

export const isInViewport = (shape: GeofenceShape<GeofenceItem>, bounds: LatLngBounds): boolean => {
  const b = shape.bounds;
  if (!b) return false;
  return (
    b.maxLat >= bounds.getSouth() &&
    b.minLat <= bounds.getNorth() &&
    b.maxLon >= bounds.getWest() &&
    b.minLon <= bounds.getEast()
  );
};

/** Parsing is keyed to the list, so panning only re-runs the cheap comparisons. */
export const useGeofenceShapes = <T extends GeofenceItem>(items: T[]): GeofenceShape<T>[] =>
  useMemo(
    () =>
      items
        // (0, 0) is the proto default, not a location: drawing it puts a pin in
        // the Atlantic and inflates the cluster counts.
        .filter(
          (i) => Number.isFinite(i.lat) && Number.isFinite(i.lon) && (i.lat !== 0 || i.lon !== 0),
        )
        .map((item) => {
          const polygons = pickupLocationPolygons(item.geometry);
          return { item, polygons, bounds: polygonsBounds(polygons) };
        }),
    [items],
  );
